import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import * as _ from 'lodash';

import { WikiService } from '../services/wiki.service';
import { SiteConfigService } from '../../../core/services/core.site-config.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';

/**
 * The result of a document import, as the host receives it.
 */
export interface WikiImportResult {
    /**
     * Identifies the pictures this import stored. They are pending: a save of the
     * page keeps those its content references, and the host discards the import
     * when the edit is cancelled. Null when the document had no pictures.
     */
    importId: string | null;
    /** The Markdown, with every picture linked to a file owned by the page. */
    content: string;
    /** One entry per picture extracted from the document. */
    images: Array<{
        filesystemId: number;
        fileName: string;
        fileSize: number;
        fileType: string;
        url: string;
    }>;
    warnings: string[];
    /** The document that was imported. */
    fileName: string;
}

/**
 * "Import document" control for the wiki editor.
 *
 * Picks a Word, PowerPoint, Excel or PDF file, uploads it through the ordinary
 * chunked upload against the page, has the backend convert it to Markdown, and
 * hands the result to the host. The host — the editor — decides what to do with
 * the Markdown; this component only knows how to get it. The pictures are
 * stored as files owned by the page by the time the result arrives, but stay
 * pending until the page is saved (see {@link WikiImportResult.importId}).
 *
 * The counterpart of `<wiki-download>`: that one exists when a converter can
 * produce documents, this one when it can read them. Both learn that from the
 * site config, which the backend keeps current with the converter's liveness.
 */
@Component({
    selector: 'wiki-import',
    template: require('./wiki.import.component.html')
})
export class WikiImportComponent implements OnInit {
    /** The page the content is for. 0 (a page not saved yet) disables the control. */
    @Input() wikiPageId: number = 0;
    /** Button classes, so the control matches the toolbar it sits in. */
    @Input() buttonClass: string = 'btn btn-default btn-sm';
    /**
     * Asked before the file picker opens. Resolving false cancels the import
     * before anything is uploaded — the host uses it to confirm what will happen
     * to content the page already has.
     */
    @Input() beforePick: () => Promise<boolean> = () => Promise.resolve(true);

    /** Fires with the converted document once the backend has answered. */
    @Output() imported = new EventEmitter<WikiImportResult>();

    @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef;

    /** File extensions a running converter can import; empty hides the control. */
    importTypes: string[] = [];
    /** Name of the document in flight, so the button can show progress. */
    importing: string = '';
    /** Which half of the work is in flight: the upload, then the conversion. */
    stage: 'uploading' | 'converting' | '' = '';
    /** Upload progress, 0-100, while uploading. */
    percent: number = 0;

    constructor(
        private wikiService: WikiService,
        private siteConfigService: SiteConfigService,
        private uploadService: UploadService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
    ) {}

    ngOnInit(): void {
        this.importTypes =
            _.get(this.siteConfigService.getSiteConfig(), 'documentImportTypes', []) || [];
    }

    /** Whether a converter is running that can import anything. */
    get available(): boolean {
        return this.importTypes.length > 0;
    }

    /** A page that has not been saved has no id for the pictures to belong to. */
    get enabled(): boolean {
        return this.available && this.wikiPageId > 0 && !this.importing;
    }

    /** The file picker's accept list, from the advertised types. */
    get accept(): string {
        return this.importTypes.map(t => '.' + t).join(',');
    }

    get title(): string {
        if (this.wikiPageId <= 0) {
            return this.gfTranslateService.translate('Save the page first, then import a document into it');
        }
        return this.gfTranslateService.translate(
            'Replace or extend this page with the content of a Word, PowerPoint, Excel or PDF document');
    }

    /** What the button says while an import runs: the stage, and for an upload how far along it is. */
    get progressLabel(): string {
        return this.stage === 'uploading'
            ? this.gfTranslateService.translate('Uploading… {{percent}}%', { percent: this.percent })
            : this.gfTranslateService.translate('Converting…');
    }

    /**
     * What a screen reader is told. The stage only: a live region announces every change to its
     * text, and the percentage changes dozens of times an upload, so reading it out would bury
     * everything else the page says. Sighted users get the percentage on the button.
     */
    get liveMessage(): string {
        if (this.stage === 'uploading') {
            return this.gfTranslateService.translate('Uploading…');
        }
        return this.stage === 'converting' ? this.gfTranslateService.translate('Converting…') : '';
    }

    openPicker(): void {
        if (!this.enabled) { return; }
        this.beforePick().then((proceed: boolean) => {
            if (proceed && this.fileInputRef) {
                this.fileInputRef.nativeElement.click();
            }
        }).catch(() => {});
    }

    onFileSelected(event: any): void {
        const files: FileList = event.target.files;
        const file = files && files.length ? files[0] : null;
        event.target.value = '';
        if (file) { this.importFile(file); }
    }

    /**
     * Upload, convert, hand over.
     *
     * The upload service resolves its promise with nothing and swallows the reason
     * when the completion callback rejects, so the backend's own message (an
     * unsupported type, a converter that is down) is captured here to be shown.
     */
    importFile(file: File): void {
        if (this.wikiPageId <= 0) { return; }
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        if (this.importTypes.indexOf(extension) === -1) {
            this.uiService.message.showError(this.gfTranslateService.translate(
                'Documents of type .{{extension}} cannot be imported. Supported: {{types}}',
                { extension, types: this.importTypes.join(', ') }));
            return;
        }

        this.importing = file.name;
        this.stage = 'uploading';
        this.percent = 0;
        let result: any = null;
        let failure: any = null;

        // UploadService.uploadFiles() calls this as (section, refId, file, bytesUploaded, bytesRemaining,
        // percentUploaded, percentRemaining); only the percentage is used here.
        const progressCallback = (_section: string, _refId: number, _file: File,
                                  _bytesUploaded: number, _bytesRemaining: number, percentUploaded: number) => {
            this.percent = Math.round(percentUploaded || 0);
        };
        const successCallback = (_section: string, _refId: number, _file: any, hash: string) => {
            this.stage = 'converting';
            return this.wikiService.importDocument(this.wikiPageId, hash, { fileName: file.name })
                .then((response: any) => { result = response; })
                .catch((err: any) => { failure = err; throw err; });
        };
        const failureCallback = (_section: string, _refId: number, _file: any, err: any) => {
            failure = failure || err || 'upload';
        };

        // Section `wikipage`: the same permission check, and the same owner for
        // the pictures, as a picture uploaded from the editor toolbar.
        this.uploadService.uploadFiles('wikipage', this.wikiPageId, [file],
            { successCallback, failureCallback, progressCallback })
            .then(() => {
                if (!result) {
                    // Rejected upload (size, permission) or a failed conversion.
                    this.showFailure(failure);
                    return;
                }
                this.imported.emit({
                    importId: result.importId || null,
                    content: String(result.content || ''),
                    images: result.images || [],
                    warnings: result.warnings || [],
                    fileName: file.name,
                });
            })
            .catch(() => this.showFailure(failure))
            .then(() => { this.importing = ''; this.stage = ''; });
    }

    private showFailure(err: any): void {
        if (err === 'size') {
            return;     // the upload service has already shown the size message
        }
        const message = _.get(err, 'error.message') || _.get(err, 'message');
        this.uiService.message.showError(
            message && typeof message === 'string'
                ? message
                : this.gfTranslateService.translate('The document could not be imported.'),
            this.gfTranslateService.translate('Import document')
        );
    }
}
